// AdjustmentDetailsPage.js - Updated with Dashboard API and Modification Warning
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;
const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL || 'http://localhost:8000';

function AdjustmentDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [booking, setBooking] = useState(location.state?.booking || null);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [paymentMetrics, setPaymentMetrics] = useState(null);
  const [categories, setCategories] = useState([]);
  const [detailedBreakdown, setDetailedBreakdown] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [reversals, setReversals] = useState([]);
  const [showRefundsSection, setShowRefundsSection] = useState(true);
  const [refunds, setRefunds] = useState([]);
  const [showAdjustmentsSection, setShowAdjustmentsSection] = useState(true);
  const [showReversalsSection, setShowReversalsSection] = useState(true);
  const [showVouchersSection, setShowVouchersSection] = useState(true);
  const [showAdjustmentForm, setShowAdjustmentForm] = useState(false);
  const [showAdjustmentHistory, setShowAdjustmentHistory] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalData, setErrorModalData] = useState(null);
  const [currentAdjustment, setCurrentAdjustment] = useState({
    amount: '',
    adjustment_type: 'flat',
    is_discount: true,
    billing_type: 'Manual Adjustment - Discount',
    base_category: '',
    reason: '',
    is_taxable: true,
    override_base_amount: 0,
    billing_id: '',
    comment: '',
    selected_order: null,
    direct_bill_adjustment: false,
  });
  const [emailStatus, setEmailStatus] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [voucherTargetCategory, setVoucherTargetCategory] = useState('');
  const [voucherReason, setVoucherReason] = useState('');
  const [transferringVoucher, setTransferringVoucher] = useState(false);
  const [amountError, setAmountError] = useState('');

  // Modification warning state
  const [modificationWarning, setModificationWarning] = useState(null);
  const [showModificationWarningModal, setShowModificationWarningModal] = useState(false);

  // New state for cancellation modals
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedAdjustment, setSelectedAdjustment] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [showCancelAllModal, setShowCancelAllModal] = useState(false);
  const [cancelAllReason, setCancelAllReason] = useState('');
  const [cancellingAll, setCancellingAll] = useState(false);

  // Voucher cancellation states
  const [showCancelVoucherModal, setShowCancelVoucherModal] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [cancelVoucherReason, setCancelVoucherReason] = useState('');
  const [cancellingVoucher, setCancellingVoucher] = useState(false);

  // State for orders data (for adjustment form)
  const [ordersData, setOrdersData] = useState({});

  // Informational only: booking has one or more modifications.
  // Used for banners/badges — must NOT gate any action.
  const hasModifications = () => {
    return modificationWarning?.has_modifications === true;
  };

  // Business rule: modifications no longer block adjustments/discounts/vouchers.
  // Single switch kept so the old behaviour can be restored in one line.
  const blocksActions = () => false;

  const getModificationMessage = () => {
    return (
      modificationWarning?.message ||
      'This booking has modifications. Adjustments, discounts and vouchers apply to the current billing amount.'
    );
  };

  // Show modification warning popup
  const showModificationPopup = () => {
    if (hasModifications()) {
      setShowModificationWarningModal(true);
    }
  };

  // Fetch current user on component mount
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await fetch(`${AQ_BASE_URL}/aq/api/currentuser`, {
          credentials: 'include',
          headers: {
            accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch user data');
        }

        const userData = await response.json();
        if (userData && userData.id) {
          setCurrentUser(userData);
          console.log('Current user loaded:', userData);
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error);
      }
    };

    fetchCurrentUser();
  }, []);

  // Cancel single adjustment
  const handleCancelAdjustment = async () => {
    if (!cancelReason || cancelReason.length < 5) {
      setEmailStatus({
        success: false,
        message: 'Please provide a reason (minimum 5 characters)',
      });
      setTimeout(() => setEmailStatus(null), 3000);
      return;
    }

    if (!currentUser || !currentUser.id) {
      setEmailStatus({
        success: false,
        message: 'Unable to identify current user. Please refresh and try again.',
      });
      setTimeout(() => setEmailStatus(null), 3000);
      return;
    }

    setCancelling(true);

    try {
      const billingId = selectedAdjustment.billing_id || selectedAdjustment.billingid;

      if (!billingId) {
        throw new Error('No billing ID found for this adjustment');
      }

      const payload = {
        bookingid: booking.bookingid,
        billing_id: billingId,
        reason: cancelReason,
        created_by: parseInt(currentUser.id),
      };

      console.log('Sending cancellation payload:', payload);

      const response = await fetch(`${BQ_BASE_URL}/bq/api/billing/cancel-adjustment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to cancel adjustment');
      }

      const result = await response.json();

      setEmailStatus({
        success: true,
        message:
          result.message ||
          `Adjustment cancelled successfully. Reversal impact: ₹${Math.abs(result.reversal_impact).toFixed(2)}`,
      });

      await fetchAllDetails(booking);

      setShowCancelModal(false);
      setSelectedAdjustment(null);
      setCancelReason('');

      setTimeout(() => setEmailStatus(null), 3000);
    } catch (err) {
      console.error('Error cancelling adjustment:', err);
      setEmailStatus({
        success: false,
        message: err.message || 'Failed to cancel adjustment',
      });
      setTimeout(() => setEmailStatus(null), 3000);
    } finally {
      setCancelling(false);
    }
  };

  // Cancel all adjustments
  const handleCancelAllAdjustments = async () => {
    if (!cancelAllReason || cancelAllReason.length < 5) {
      setEmailStatus({
        success: false,
        message: 'Please provide a reason (minimum 5 characters)',
      });
      setTimeout(() => setEmailStatus(null), 3000);
      return;
    }

    if (!currentUser || !currentUser.id) {
      setEmailStatus({
        success: false,
        message: 'Unable to identify current user. Please refresh and try again.',
      });
      setTimeout(() => setEmailStatus(null), 3000);
      return;
    }

    setCancellingAll(true);

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/billing/cancel-all-adjustments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({
          bookingid: booking.bookingid,
          reason: cancelAllReason,
          created_by: parseInt(currentUser.id),
          confirm: true,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to cancel all adjustments');
      }

      const result = await response.json();

      setEmailStatus({
        success: true,
        message:
          result.message ||
          `Successfully cancelled ${result.cancelled_count} adjustments. Total reversal impact: ₹${Math.abs(result.reversal_impact).toFixed(2)}`,
      });

      await fetchAllDetails(booking);

      setShowCancelAllModal(false);
      setCancelAllReason('');

      setTimeout(() => setEmailStatus(null), 3000);
    } catch (err) {
      console.error('Error cancelling all adjustments:', err);
      setEmailStatus({
        success: false,
        message: err.message || 'Failed to cancel all adjustments',
      });
      setTimeout(() => setEmailStatus(null), 3000);
    } finally {
      setCancellingAll(false);
    }
  };

  // Cancel single voucher using /bq/api/billing/voucher/cancel
  const handleCancelVoucher = async () => {
    if (!cancelVoucherReason || cancelVoucherReason.length < 5) {
      setEmailStatus({
        success: false,
        message: 'Please provide a reason (minimum 5 characters)',
      });
      setTimeout(() => setEmailStatus(null), 3000);
      return;
    }

    if (!currentUser || !currentUser.id) {
      setEmailStatus({
        success: false,
        message: 'Unable to identify current user. Please refresh and try again.',
      });
      setTimeout(() => setEmailStatus(null), 3000);
      return;
    }

    setCancellingVoucher(true);

    try {
      const payload = {
        bookingid: booking.bookingid,
        voucher_code: selectedVoucher.voucher_code,
        reason: cancelVoucherReason,
        created_by: parseInt(currentUser.id),
      };

      const response = await fetch(`${BQ_BASE_URL}/bq/api/billing/voucher/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to cancel voucher');
      }

      const result = await response.json();

      setEmailStatus({
        success: true,
        message: result.message || `Voucher ${result.voucher_code} cancelled successfully`,
      });

      await fetchAllDetails(booking);

      setShowCancelVoucherModal(false);
      setSelectedVoucher(null);
      setCancelVoucherReason('');

      setTimeout(() => setEmailStatus(null), 3000);
    } catch (err) {
      console.error('Error cancelling voucher:', err);
      setEmailStatus({
        success: false,
        message: err.message || 'Failed to cancel voucher',
      });
      setTimeout(() => setEmailStatus(null), 3000);
    } finally {
      setCancellingVoucher(false);
    }
  };

  const handleCorporateDiscountTransfer = async () => {
    if (blocksActions()) {
      showModificationPopup();
      return;
    }

    if (!voucherTargetCategory) {
      setEmailStatus({
        success: false,
        message: 'Please select a target category for the voucher',
      });
      setTimeout(() => setEmailStatus(null), 3000);
      return;
    }

    if (!voucherReason) {
      setEmailStatus({
        success: false,
        message: 'Please enter a reason for the voucher',
      });
      setTimeout(() => setEmailStatus(null), 3000);
      return;
    }

    if (!currentUser || !currentUser.id) {
      setEmailStatus({
        success: false,
        message: 'Unable to identify current user. Please refresh and try again.',
      });
      setTimeout(() => setEmailStatus(null), 3000);
      return;
    }

    setTransferringVoucher(true);

    try {
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/billing/voucher/corporate-discount-transfer`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            accept: 'application/json',
          },
          body: JSON.stringify({
            bookingid: booking.bookingid,
            target_category: voucherTargetCategory,
            reason: voucherReason,
            created_by: parseInt(currentUser.id),
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to transfer corporate discount');
      }

      const result = await response.json();

      setEmailStatus({
        success: true,
        message: result.message || 'Corporate discount transferred successfully',
      });

      await fetchAllDetails(booking);

      setShowVoucherModal(false);
      setVoucherTargetCategory('');
      setVoucherReason('');

      setTimeout(() => setEmailStatus(null), 3000);
    } catch (err) {
      console.error('Error transferring discount:', err);
      setEmailStatus({
        success: false,
        message: err.message || 'Failed to transfer corporate discount',
      });
      setTimeout(() => setEmailStatus(null), 3000);
    } finally {
      setTransferringVoucher(false);
    }
  };

  useEffect(() => {
    if (booking) {
      fetchAllDetails();
    } else {
      const params = new URLSearchParams(location.search);
      const orderId = params.get('orderId');
      if (orderId) {
        fetchBookingByOrderId(orderId);
      } else {
        setLoading(false);
      }
    }
  }, [booking]);

  const fetchBookingByOrderId = async (orderId) => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/bookings/`, {
        headers: { accept: 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        const foundBooking = data.bookings.find((b) => b.orderid === orderId);
        if (foundBooking) {
          setBooking(foundBooking);
          fetchAllDetails(foundBooking);
        }
      }
    } catch (err) {
      console.error('Error fetching booking:', err);
    }
  };

  // In the fetchAllDetails function, add modification warning to state
  const fetchAllDetails = async (selectedBooking = booking) => {
    setLoading(true);
    try {
      const dashboardResponse = await fetch(
        `${BQ_BASE_URL}/bq/api/dashboard/booking/${selectedBooking.bookingid}`,
        { headers: { accept: 'application/json' } }
      );

      if (dashboardResponse.ok) {
        const dashboardDataResult = await dashboardResponse.json();
        setDashboardData(dashboardDataResult);

        setPaymentMetrics(dashboardDataResult.payment_metrics);
        setCategories(dashboardDataResult.payment_metrics?.categories || []);
        setDetailedBreakdown(dashboardDataResult.payment_metrics?.detailed_breakdown || []);

        setAdjustments(dashboardDataResult.adjustments || []);
        setReversals(dashboardDataResult.reversals || []);
        setRefunds(dashboardDataResult.refunds || []);
        setVouchers(dashboardDataResult.vouchers || []);

        // Set modification warning
        if (dashboardDataResult.modification_warning) {
          setModificationWarning(dashboardDataResult.modification_warning);
        } else {
          setModificationWarning(null);
        }

        // Build ordersData from detailed_breakdown - Map category to display format
        const ordersMap = {};
        if (dashboardDataResult.payment_metrics?.detailed_breakdown) {
          dashboardDataResult.payment_metrics.detailed_breakdown.forEach((item) => {
            // Map API category to display format
            let displayCategory = item.category;
            switch (item.category) {
              case 'ROOM TARIFF':
                displayCategory = 'Room Rent';
                break;
              case 'FOOD CHARGES':
                displayCategory = 'Food Order';
                break;
              case 'AMENITY CHARGES':
                displayCategory = 'Amenity Service';
                break;
              case 'ROOM SERVICE CHARGES':
                displayCategory = 'Room Service';
                break;
              default:
                displayCategory = item.category;
            }

            if (!ordersMap[displayCategory]) {
              ordersMap[displayCategory] = [];
            }
            ordersMap[displayCategory].push({
              billing_id: item.billing_id,
              order_detail_id: item.order_detail_id,
              description: item.description,
              totalamount: parseFloat(item.total_amount),
              base_amount: parseFloat(item.base_amount),
              tax_amount: parseFloat(item.tax_amount),
              discount: parseFloat(item.discount),
              created_at: new Date().toISOString(),
            });
          });
        }
        setOrdersData(ordersMap);
      }

      const invoiceResponse = await fetch(
        `${BQ_BASE_URL}/bq/api/invoice/data/${selectedBooking.orderid}?format=folio&room_rent=true&food_order=true&amenity_service=true&room_service=true&full_stay=false`,
        { headers: { accept: 'application/json' } }
      );

      if (invoiceResponse.ok) {
        const invoiceDataResult = await invoiceResponse.json();
        setInvoiceData(invoiceDataResult);
      }
    } catch (err) {
      console.error('Error fetching details:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateAmountOnChange = (value) => {
    if (!currentAdjustment.selected_order) {
      setAmountError('');
      return;
    }

    const billingRecordAmount = parseFloat(currentAdjustment.selected_order.totalamount);

    if (currentAdjustment.is_discount) {
      let discountAmount = parseFloat(value);

      if (currentAdjustment.adjustment_type === 'percentage') {
        discountAmount = (parseFloat(value) / 100) * billingRecordAmount;
      }

      if (!isNaN(discountAmount) && discountAmount > billingRecordAmount) {
        setAmountError(`Discount cannot exceed ₹${billingRecordAmount.toFixed(2)}`);
        return false;
      } else {
        setAmountError('');
        return true;
      }
    }

    setAmountError('');
    return true;
  };
  // Add this validation function before handleAmountChange
  const validatePercentageAmount = (value, selectedOrder, adjustmentType) => {
    if (adjustmentType !== 'percentage') return true;
    if (!selectedOrder) return true;

    const percentageValue = parseFloat(value);
    const billingRecordAmount = parseFloat(selectedOrder.totalamount);

    // Validate percentage range (0-100)
    if (isNaN(percentageValue)) return true;
    if (percentageValue > 100) {
      setAmountError(`Percentage cannot exceed 100%`);
      return false;
    }
    if (percentageValue < 0) {
      setAmountError(`Percentage cannot be negative`);
      return false;
    }

    // Calculate the actual discount amount for the "will apply" message
    const discountAmount = (percentageValue / 100) * billingRecordAmount;

    if (discountAmount > billingRecordAmount) {
      setAmountError(`Discount cannot exceed ${formatCurrency(billingRecordAmount)}`);
      return false;
    }

    setAmountError('');
    return true;
  };

  // Update handleAmountChange function
  const handleAmountChange = (e) => {
    const value = e.target.value;
    setCurrentAdjustment({
      ...currentAdjustment,
      amount: value,
    });

    if (!currentAdjustment.selected_order) {
      setAmountError('');
      return;
    }

    const billingRecordAmount = parseFloat(currentAdjustment.selected_order.totalamount);

    if (currentAdjustment.adjustment_type === 'percentage') {
      const percentageValue = parseFloat(value);

      if (value && !isNaN(percentageValue)) {
        // Check if percentage exceeds 100
        if (percentageValue > 100) {
          setAmountError(`Percentage cannot exceed 100%`);
          return;
        }
        if (percentageValue < 0) {
          setAmountError(`Percentage cannot be negative`);
          return;
        }

        // Calculate discount amount for validation
        const discountAmount = (percentageValue / 100) * billingRecordAmount;
        if (discountAmount > billingRecordAmount) {
          setAmountError(`Discount cannot exceed ${formatCurrency(billingRecordAmount)}`);
          return;
        }
      }
      setAmountError('');
    } else if (currentAdjustment.adjustment_type === 'flat') {
      const flatAmount = parseFloat(value);
      if (currentAdjustment.is_discount && value && !isNaN(flatAmount)) {
        if (flatAmount > billingRecordAmount) {
          setAmountError(`Discount cannot exceed ${formatCurrency(billingRecordAmount)}`);
          return;
        }
      }
      setAmountError('');
    }
  };

  const validateDiscountAmount = (amount, selectedOrder, adjustmentType, isDiscount) => {
    if (!isDiscount) return true;
    if (!selectedOrder) return true;

    const billingRecordAmount = parseFloat(selectedOrder.totalamount);
    let discountAmount = amount;

    if (adjustmentType === 'percentage') {
      discountAmount = (amount / 100) * billingRecordAmount;
    }

    if (discountAmount > billingRecordAmount) {
      setAmountError(`Discount cannot exceed ₹${billingRecordAmount.toFixed(2)}`);
      return false;
    }

    return true;
  };
  // Update isFormValid function
  const isFormValid = () => {
    if (blocksActions()) return false;
    if (amountError) return false;
    if (!currentAdjustment.amount || parseFloat(currentAdjustment.amount) <= 0) return false;

    if (!currentAdjustment.selected_order) return false;
    if (!currentAdjustment.reason) return false;
    if (!currentAdjustment.base_category) return false;
    if (!currentUser) return false;

    const amountValue = parseFloat(currentAdjustment.amount);
    const billingRecordAmount = parseFloat(currentAdjustment.selected_order.totalamount);

    // Validate percentage range
    if (currentAdjustment.adjustment_type === 'percentage') {
      if (amountValue > 100) return false;
      if (amountValue < 0) return false;

      const discountAmount = (amountValue / 100) * billingRecordAmount;
      if (discountAmount > billingRecordAmount) return false;
    } else if (currentAdjustment.adjustment_type === 'flat' && currentAdjustment.is_discount) {
      if (amountValue > billingRecordAmount) return false;
    }

    return true;
  };

  const handleAddAdjustment = async () => {
    if (blocksActions()) {
      showModificationPopup();
      return;
    }

    if (
      !currentAdjustment.reason ||
      !currentAdjustment.base_category ||
      !currentAdjustment.selected_order
    ) {
      setErrorModalData({
        title: 'Validation Error',
        message: 'Please fill all required fields',
        details: null,
      });
      setShowErrorModal(true);
      return;
    }

    if (!currentUser || !currentUser.id) {
      setErrorModalData({
        title: 'User Error',
        message: 'Unable to identify current user. Please refresh and try again.',
        details: null,
      });
      setShowErrorModal(true);
      return;
    }

    const amountValue = parseFloat(currentAdjustment.amount);
    const billingRecordAmount = parseFloat(currentAdjustment.selected_order.totalamount);

    // Final validation before sending
    if (currentAdjustment.adjustment_type === 'percentage') {
      if (amountValue > 100) {
        setAmountError(`Percentage cannot exceed 100%`);
        return;
      }
      if (amountValue < 0) {
        setAmountError(`Percentage cannot be negative`);
        return;
      }

      const discountAmount = (amountValue / 100) * billingRecordAmount;
      if (discountAmount > billingRecordAmount) {
        setAmountError(`Discount cannot exceed ${formatCurrency(billingRecordAmount)}`);
        return;
      }
    }

    if (isNaN(amountValue) || amountValue <= 0) {
      setAmountError('Please enter a valid amount');
      return;
    }

    try {
      const adjustmentPayload = {
        bookingid: booking.bookingid,
        amount: amountValue, // Send raw value (10 for 10%, 500 for ₹500)
        adjustment_type: currentAdjustment.adjustment_type,
        is_discount: currentAdjustment.is_discount,
        billing_type: currentAdjustment.billing_type,
        base_category: currentAdjustment.base_category,
        reason: currentAdjustment.reason,
        is_taxable: currentAdjustment.is_taxable,
        billing_id: currentAdjustment.selected_order.billing_id,
        comment: currentAdjustment.comment,
        created_by: parseInt(currentUser.id),
      };

      if (currentAdjustment.adjustment_type === 'percentage') {
        adjustmentPayload.override_base_amount = currentAdjustment.selected_order.totalamount;
      }

      console.log('Sending adjustment payload:', adjustmentPayload);

      const response = await fetch(`${BQ_BASE_URL}/bq/api/billing/manual-adjustment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify(adjustmentPayload),
      });

      if (!response.ok) {
        const errorData = await response.json();

        if (errorData.detail?.code === 'DISCOUNT_EXCEEDS_CATEGORY_TOTAL') {
          setAmountError(
            errorData.detail.message || 'Discount exceeds the allowed limit for this category.'
          );
          return;
        } else if (typeof errorData.detail === 'string') {
          setAmountError(errorData.detail);
          return;
        } else if (errorData.detail?.message) {
          setAmountError(errorData.detail.message);
          return;
        } else {
          setErrorModalData({
            title: 'Error',
            message: 'Failed to add adjustment',
            details: errorData,
          });
          setShowErrorModal(true);
          return;
        }
      }

      const result = await response.json();

      await fetchAllDetails(booking);

      setCurrentAdjustment({
        amount: '',
        adjustment_type: 'flat',
        is_discount: true,
        billing_type: 'Manual Adjustment - Discount',
        base_category: '',
        reason: '',
        is_taxable: true,
        override_base_amount: 0,
        billing_id: '',
        comment: '',
        selected_order: null,
        direct_bill_adjustment: false,
      });

      setAmountError('');

      setEmailStatus({
        success: true,
        message: 'Adjustment added successfully',
      });
      setTimeout(() => setEmailStatus(null), 3000);

      setShowAdjustmentForm(false);
    } catch (err) {
      console.error('Error adding adjustment:', err);
      setAmountError(err.message || 'Failed to add adjustment');
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₹0.00';
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return dateStr;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      Soft: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Reserved' },
      Hard: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Checked In' },
      CheckedOut: {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        label: 'Checked Out',
      },
      Cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled' },
    };
    const config = statusConfig[status] || {
      bg: 'bg-gray-100',
      text: 'text-gray-800',
      label: status,
    };
    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  // Replace the uniqueOrderTypes mapping
  const uniqueOrderTypes = Object.keys(ordersData)
    .filter((type) => type !== 'Manual Adjustment')
    .map((type) => {
      // Map the category names
      switch (type) {
        case 'ROOM TARIFF':
          return 'Room Rent';
        case 'FOOD CHARGES':
          return 'Food Order';
        case 'AMENITY CHARGES':
          return 'Amenity Service';
        case 'ROOM SERVICE CHARGES':
          return 'Room Service';
        default:
          return type;
      }
    });

  const renderCategoryBreakdown = () => {
    if (!categories || categories.length === 0) return null;

    return categories.map((category, idx) => {
      const showVoucherBadge = category.voucher_applied;
      const cashPaid = category.cash_paid ? parseFloat(category.cash_paid) : 0;
      const voucherUsed = category.voucher_used ? parseFloat(category.voucher_used) : 0;
      const voucherRemaining = category.voucher_remaining
        ? parseFloat(category.voucher_remaining)
        : 0;

      return (
        <div key={idx} className="mb-4 border border-gray-200 rounded-xl overflow-hidden bg-white">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <div className="flex justify-between items-center flex-wrap gap-2">
              <h3 className="font-semibold text-gray-900">
                {category.category.replace(/_/g, ' ')}
              </h3>
              <div className="flex gap-2">
                {showVoucherBadge && (
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 5v2m0 4v2m0 4v2M5 5h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z"
                      />
                    </svg>
                    Voucher Applied
                  </span>
                )}
              </div>
            </div>

            {/* Show Voucher Used and Remaining ONLY if voucher_applied is true */}
            {showVoucherBadge && (
              <div className="mt-2 text-xs text-gray-600">
                <span className="font-medium">Cash Paid:</span> {formatCurrency(cashPaid)}
                <span className="font-medium ml-3">Voucher Used:</span>{' '}
                {formatCurrency(voucherUsed)}
                {voucherRemaining > 0 && (
                  <span className="ml-3">
                    <span className="font-medium">Remaining:</span>{' '}
                    {formatCurrency(voucherRemaining)}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-gray-600 text-xs font-medium uppercase tracking-wider">
                    Description
                  </th>
                  <th className="text-right py-3 px-4 text-gray-600 text-xs font-medium uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="text-right py-3 px-4 text-gray-600 text-xs font-medium uppercase tracking-wider">
                    Tax
                  </th>
                  <th className="text-right py-3 px-4 text-gray-600 text-xs font-medium uppercase tracking-wider">
                    Discount
                  </th>
                  <th className="text-right py-3 px-4 text-gray-600 text-xs font-medium uppercase tracking-wider">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {detailedBreakdown
                  .filter((item) => item.category === category.category)
                  .map((item, itemIdx) => (
                    <tr
                      key={itemIdx}
                      className="border-t border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-3 px-4 text-gray-900 text-sm">{item.description}</td>
                      <td className="py-3 px-4 text-right text-gray-700 text-sm">
                        {formatCurrency(parseFloat(item.base_amount))}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-700 text-sm">
                        {formatCurrency(parseFloat(item.tax_amount))}
                      </td>
                      <td className="py-3 px-4 text-right text-red-600 text-sm">
                        {formatCurrency(parseFloat(item.discount))}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900 text-sm font-semibold">
                        {formatCurrency(parseFloat(item.total_amount))}
                      </td>
                    </tr>
                  ))}
                <tr className="border-t border-gray-200 bg-gray-50 font-semibold">
                  <td className="py-3 px-4 text-gray-900">Total</td>
                  <td className="py-3 px-4 text-right text-gray-900">
                    {formatCurrency(parseFloat(category.base_amount))}
                  </td>
                  <td className="py-3 px-4 text-right text-gray-900">
                    {formatCurrency(parseFloat(category.tax_amount))}
                  </td>
                  <td className="py-3 px-4 text-right text-red-600">
                    {formatCurrency(parseFloat(category.discount))}
                  </td>
                  <td className="py-3 px-4 text-right text-gray-900">
                    {formatCurrency(parseFloat(category.total_amount))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 shadow-xl">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
          <p className="text-center mt-4 text-gray-600">Loading details...</p>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="bg-white rounded-2xl p-8 text-center shadow-xl">
          <p className="text-gray-600 mb-4">No booking data found.</p>
          <button
            onClick={() => navigate('/dashboard/core-modules/finance/billing-invoicing')}
            className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Modification Warning Modal */}
      {showModificationWarningModal && modificationWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center">
                <div className="bg-orange-100 rounded-full p-2 mr-3">
                  <svg
                    className="w-6 h-6 text-orange-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-black">Booking Modification Warning</h3>
              </div>
              <button
                onClick={() => setShowModificationWarningModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded">
                <div className="flex items-start">
                  <div className="flex-1">
                    <p className="text-orange-800 font-medium mb-2">
                      {modificationWarning.message}
                    </p>
                    {modificationWarning.modification_types &&
                      modificationWarning.modification_types.length > 0 && (
                        <div className="mt-2">
                          <p className="text-sm text-orange-700 font-medium">
                            Modification Details:
                          </p>
                          <ul className="list-disc list-inside text-sm text-orange-600 mt-1">
                            {modificationWarning.modification_types.map((type, index) => (
                              <li key={index}>{type}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setShowModificationWarningModal(false)}
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Message */}
      {emailStatus && (
        <div
          className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
            emailStatus.success
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          <div className="flex items-center">
            {emailStatus.success ? (
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : (
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            )}
            {emailStatus.message}
          </div>
        </div>
      )}

      {/* Cancel Single Adjustment Modal */}
      {showCancelModal && selectedAdjustment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Cancel Adjustment</h3>
              </div>
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setSelectedAdjustment(null);
                  setCancelReason('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="mb-4">
              <p className="text-gray-700 mb-3">Are you sure you want to cancel this adjustment?</p>
              <div className="bg-gray-50 p-3 rounded-lg mb-3">
                <p className="text-sm font-medium text-gray-900">
                  {selectedAdjustment.billing_type}
                </p>
                <p className="text-sm text-gray-600 mt-1">Reason: {selectedAdjustment.reason}</p>
                <p className="text-sm font-bold mt-2">
                  Amount: {formatCurrency(Math.abs(parseFloat(selectedAdjustment.amount)))}
                </p>
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cancellation Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows="3"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="Please provide a reason for cancelling this adjustment..."
              />
              <p className="text-xs text-gray-500 mt-1">Minimum 5 characters required</p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setSelectedAdjustment(null);
                  setCancelReason('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                No, Keep It
              </button>
              <button
                onClick={handleCancelAdjustment}
                disabled={cancelling || cancelReason.length < 5}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {cancelling ? (
                  <>
                    {' '}
                    <svg
                      className="w-4 h-4 mr-2 animate-spin"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>{' '}
                    Cancelling...
                  </>
                ) : (
                  'Yes, Cancel Adjustment'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Single Voucher Modal */}
      {showCancelVoucherModal && selectedVoucher && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Cancel Voucher</h3>
              </div>
              <button
                onClick={() => {
                  setShowCancelVoucherModal(false);
                  setSelectedVoucher(null);
                  setCancelVoucherReason('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="mb-4">
              <p className="text-gray-700 mb-3">Are you sure you want to cancel this voucher?</p>
              <div className="bg-gray-50 p-3 rounded-lg mb-3">
                <p className="text-sm font-medium text-gray-900">
                  Voucher: {selectedVoucher.voucher_code}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Target Category: {selectedVoucher.target_category}
                </p>
                <p className="text-sm text-gray-600">Reason: {selectedVoucher.reason}</p>
                <p className="text-sm font-bold mt-2">
                  Amount: {formatCurrency(parseFloat(selectedVoucher.amount))}
                </p>
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cancellation Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={cancelVoucherReason}
                onChange={(e) => setCancelVoucherReason(e.target.value)}
                rows="3"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="Please provide a reason for cancelling this voucher..."
              />
              <p className="text-xs text-gray-500 mt-1">Minimum 5 characters required</p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCancelVoucherModal(false);
                  setSelectedVoucher(null);
                  setCancelVoucherReason('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                No, Keep It
              </button>
              <button
                onClick={handleCancelVoucher}
                disabled={cancellingVoucher || cancelVoucherReason.length < 5}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {cancellingVoucher ? (
                  <>
                    {' '}
                    <svg
                      className="w-4 h-4 mr-2 animate-spin"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>{' '}
                    Cancelling...
                  </>
                ) : (
                  'Yes, Cancel Voucher'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel All Adjustments Modal */}
      {showCancelAllModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Cancel All Adjustments</h3>
              </div>
              <button
                onClick={() => {
                  setShowCancelAllModal(false);
                  setCancelAllReason('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="mb-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-start space-x-2">
                  <svg
                    className="w-5 h-5 text-red-600 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="text-sm text-red-800">
                    <p className="font-medium">Warning: This action cannot be undone!</p>
                    <p className="mt-1">
                      This will cancel ALL manual adjustments for this booking and create reversal
                      entries.
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 mb-3">
                You are about to cancel <strong>{adjustments.length}</strong> adjustment(s):
              </p>
              <div className="bg-gray-50 p-3 rounded-lg mb-4 max-h-48 overflow-y-auto">
                {adjustments.map((adj, idx) => (
                  <div key={idx} className="text-sm py-2 border-b border-gray-200 last:border-0">
                    <p className="font-medium text-gray-900">{adj.billing_type}</p>
                    <p className="text-gray-600 text-xs">Reason: {adj.reason}</p>
                    <p className="text-gray-700 text-xs font-semibold mt-1">
                      Amount: {formatCurrency(Math.abs(parseFloat(adj.amount)))}
                    </p>
                  </div>
                ))}
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cancellation Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={cancelAllReason}
                onChange={(e) => setCancelAllReason(e.target.value)}
                rows="3"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="Please provide a reason for cancelling all adjustments..."
              />
              <p className="text-xs text-gray-500 mt-1">Minimum 5 characters required</p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCancelAllModal(false);
                  setCancelAllReason('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                No, Keep Them
              </button>
              <button
                onClick={handleCancelAllAdjustments}
                disabled={cancellingAll || cancelAllReason.length < 5}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {cancellingAll ? (
                  <>
                    {' '}
                    <svg
                      className="w-4 h-4 mr-2 animate-spin"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>{' '}
                    Cancelling All...
                  </>
                ) : (
                  'Yes, Cancel All Adjustments'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Back Button */}
      <div className="bg-white shadow-sm sticky top-0 z-10 border-b border-gray-200">
        <div className="px-6 py-4">
          <button
            onClick={() => navigate('/dashboard/core-modules/finance/billing-invoicing')}
            className="flex items-center space-x-2 text-gray-700 hover:text-black transition-colors group"
          >
            <svg
              className="w-5 h-5 group-hover:-translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            <span className="font-medium">Back to Finance & Accounting</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6 mx-auto">
        {/* Header Card - with modification warning indicator */}
        <div
          className={`bg-gray-50 rounded-2xl shadow-sm p-6 mb-6 border ${hasModifications() ? 'border-orange-300' : 'border-gray-200'}`}
        >
          <div className="flex justify-between items-start">
            <div className="flex items-center space-x-4">
              <div
                className={`w-16 h-16 ${hasModifications() ? 'bg-orange-600' : 'bg-black'} rounded-2xl flex items-center justify-center text-white text-3xl`}
              >
                {dashboardData?.guest_details?.guest_name?.[0] || booking.firstname?.[0]}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {dashboardData?.guest_details?.guest_name ||
                    `${booking.firstname} ${booking.lastname}`}
                </h1>
                <p className="text-gray-500 mt-1">
                  <span className="font-mono bg-gray-200 px-2 py-1 rounded">
                    {dashboardData?.guest_details?.order_id || booking.orderid}
                  </span>{' '}
                  • Room:{' '}
                  {dashboardData?.guest_details?.room_number ||
                    booking.roomnumber ||
                    'Not assigned'}{' '}
                  ({dashboardData?.guest_details?.room_type || booking.roomtypename})
                </p>
                {hasModifications() && (
                  <div className="mt-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      <svg
                        className="w-3 h-3 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      Modified Booking
                    </span>
                  </div>
                )}
              </div>
            </div>
            {getStatusBadge(dashboardData?.guest_details?.booking_status || booking.booking_status)}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Guest & Reservation Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Guest Details Card - Using Dashboard Data */}
            <div className="bg-gray-50 rounded-2xl shadow-sm overflow-hidden border border-gray-200">
              <div className="bg-gray-100 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center space-x-2">
                  <svg
                    className="w-6 h-6 text-gray-700"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  <h2 className="text-xl font-semibold text-gray-900">Guest Details</h2>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <svg
                        className="w-5 h-5 text-gray-400 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                          Email Address
                        </p>
                        <p className="text-gray-900 font-medium">
                          {dashboardData?.guest_details?.email || booking.emailid}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <svg
                        className="w-5 h-5 text-gray-400 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                          Phone Number
                        </p>
                        <p className="text-gray-900 font-medium">
                          {dashboardData?.guest_details?.phone || booking.phonenumber || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <svg
                        className="w-5 h-5 text-gray-400 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Client Type</p>
                        <p className="text-gray-900 font-medium">
                          {dashboardData?.guest_details?.client_type || 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <svg
                        className="w-5 h-5 text-gray-400 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Company</p>
                        <p className="text-gray-900 font-medium">
                          {dashboardData?.guest_details?.company || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Reservation Details Card */}
            <div className="bg-gray-50 rounded-2xl shadow-sm overflow-hidden border border-gray-200">
              <div className="bg-gray-100 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center space-x-2">
                  <svg
                    className="w-6 h-6 text-gray-700"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <h2 className="text-xl font-semibold text-gray-900">Reservation Details</h2>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Check In</p>
                      <p className="text-gray-900 font-semibold text-lg">
                        {formatDate(dashboardData?.guest_details?.check_in || booking.checkindate)}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                        Check Out
                      </p>
                      <p className="text-gray-900 font-semibold text-lg">
                        {formatDate(
                          dashboardData?.guest_details?.check_out || booking.checkoutdate
                        )}
                      </p>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Length of Stay</span>
                      <span className="text-gray-900 font-semibold">
                        {dashboardData?.guest_details?.length_of_stay || booking.lengthofstay}{' '}
                        nights
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Number of Guests</span>
                      <span className="text-gray-900 font-semibold">
                        {dashboardData?.guest_details?.number_of_guests || booking.numberofguests}{' '}
                        guests
                      </span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                        Room Type
                      </p>
                      <p className="text-gray-900 font-semibold">
                        {dashboardData?.guest_details?.room_type || booking.roomtypename}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                        Reservation Type
                      </p>
                      <p className="text-gray-900 font-semibold">
                        {dashboardData?.guest_details?.booking_type || booking.booking_type}
                      </p>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Reservation Made</span>
                      <span className="text-gray-900">
                        {formatDate(
                          dashboardData?.guest_details?.reservation_made_on ||
                            booking.reservationmadeon
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-gray-600">Room Number</span>
                      <span className="text-gray-900 font-mono font-semibold">
                        {dashboardData?.guest_details?.room_number ||
                          booking.roomnumber ||
                          'Not assigned'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Billing Summary Section - Using Payment Metrics from Dashboard API */}
            <div className="bg-gray-50 rounded-2xl shadow-sm overflow-hidden border border-gray-200">
              <div className="bg-gray-100 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center space-x-2">
                  <svg
                    className="w-6 h-6 text-gray-700"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  <h2 className="text-xl font-semibold text-gray-900">Billing Summary</h2>
                </div>
              </div>
              <div className="p-6">
                {/* Summary Cards - All Payment Metrics */}
                {paymentMetrics && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                      <div className="bg-white p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                          Total Amount
                        </p>
                        <p className="text-2xl font-bold text-gray-900">
                          {formatCurrency(parseFloat(paymentMetrics.bill_amount))}
                        </p>
                      </div>

                      {/* Amount Paid Card with hover info */}
                      <div className="bg-white p-4 rounded-xl border border-gray-200 relative group">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Amount Paid
                          </p>
                          <div className="relative">
                            <svg
                              className="w-4 h-4 text-gray-600 cursor-help hover:text-gray-800 transition-colors"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            {/* Tooltip on hover */}
                            <div className="absolute right-25 bottom-full w-64 bg-gray-900 text-white text-sm rounded-lg shadow-xl p-3 z-30 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200">
                              <div className="space-y-2 text-xs">
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-300">Cash Paid:</span>
                                  <span className="font-semibold text-white">
                                    {formatCurrency(parseFloat(paymentMetrics.total_cash_paid))}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center pt-1 border-t border-gray-700">
                                  <span className="text-gray-300">Voucher Used:</span>
                                  <span className="font-semibold text-white">
                                    {formatCurrency(parseFloat(paymentMetrics.total_voucher_used))}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center pt-1 border-t border-gray-700">
                                  <span className="text-gray-300">Amount Paid:</span>
                                  <span className="font-semibold text-green-400">
                                    {formatCurrency(parseFloat(paymentMetrics.paid_amount))}
                                  </span>
                                </div>
                              </div>
                              <div className="absolute right-2 -top-1 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                            </div>
                          </div>
                        </div>
                        <p className="text-2xl font-bold text-green-600 mt-1">
                          {formatCurrency(parseFloat(paymentMetrics.paid_amount))}
                        </p>
                      </div>

                      <div className="bg-white p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Amount Due</p>
                        <p
                          className={`text-2xl font-bold ${parseFloat(paymentMetrics.balance_due) > 0 ? 'text-red-600' : 'text-green-600'}`}
                        >
                          {formatCurrency(parseFloat(paymentMetrics.balance_due))}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-white p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                          Total Discounts
                        </p>
                        <p className="text-2xl font-bold text-purple-600">
                          {formatCurrency(parseFloat(paymentMetrics.total_discounts))}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">All discounts applied</p>
                      </div>
                      <div className="bg-white p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                          Voucher Amount
                        </p>
                        <p className="text-2xl font-bold text-blue-600">
                          {formatCurrency(parseFloat(paymentMetrics.voucher_amount))}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">Discount vouchers applied</p>
                      </div>
                      <div className="bg-white p-4 rounded-xl border border-gray-200">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                          Refund Amount
                        </p>
                        <p className="text-2xl font-bold text-orange-600">
                          {formatCurrency(parseFloat(paymentMetrics.refund_amount))}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">Total refunds issued</p>
                      </div>
                    </div>
                  </>
                )}

                {/* Category Breakdown from Dashboard API */}
                {renderCategoryBreakdown()}
              </div>
            </div>
          </div>

          {/* Right Column - Adjustments */}
          <div className="space-y-6">
            {/* Modification Warning Banner (optional - shows at top if booking has modifications) */}
            {hasModifications() && (
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mx-6 mt-6 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-orange-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-orange-800 font-medium">{getModificationMessage()}</p>
                  </div>
                </div>
              </div>
            )}
            {/* Add Adjustment Card - Disabled if modifications exist */}
            <div className="bg-gray-50 rounded-2xl shadow-sm overflow-hidden border border-gray-200 sticky top-24">
              <div className="bg-gray-100 px-4 sm:px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <svg
                      className="w-5 h-5 text-gray-700"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div className="flex items-center space-x-2">
                      <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                        Adjustments
                      </h2>
                    </div>
                  </div>
                  {hasModifications() && (
                    <div className="relative group">
                      <svg
                        className="w-5 h-5 text-orange-500 cursor-help"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1 text-left">
                  Discounts or charges applied by staff
                </p>
              </div>
              <div className="p-4 sm:p-6">
                <div className="relative group w-full">
                  <button
                    onClick={() => {
                      if (blocksActions()) {
                        showModificationPopup();
                      } else {
                        setShowAdjustmentForm(!showAdjustmentForm);
                      }
                    }}
                    disabled={blocksActions()}
                    className={`w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 shadow-sm ${
                      blocksActions()
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-black text-white hover:bg-gray-800'
                    }`}
                  >
                    <div
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${blocksActions() ? 'border-gray-500' : 'border-white'}`}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                      >
                        {showAdjustmentForm ? (
                          <line x1="5" y1="12" x2="19" y2="12" />
                        ) : (
                          <>
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                          </>
                        )}
                      </svg>
                    </div>
                    <span>
                      {blocksActions()
                        ? 'Adjustments Disabled'
                        : showAdjustmentForm
                          ? 'Hide Adjustment'
                          : 'Add Adjustment'}
                    </span>
                  </button>
                  {blocksActions() && (
                    <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded-lg py-2 px-3 w-72 z-[9999] shadow-xl pointer-events-none">
                      <div className="font-medium text-center">{getModificationMessage()}</div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                    </div>
                  )}
                </div>

                {/* Adjustment Form - Disabled inputs if modifications exist */}
                {showAdjustmentForm && (
                  <div className="mt-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        value={currentAdjustment.base_category}
                        onChange={(e) => {
                          const category = e.target.value;
                          setCurrentAdjustment({
                            ...currentAdjustment,
                            base_category: category,
                            billing_id: '',
                            selected_order: null,
                          });
                          setAmountError('');
                        }}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-black bg-white text-sm sm:text-base"
                      >
                        <option value="">Select category</option>
                        <option value="Room Rent">Room Rent</option>
                        <option value="Food Order">Food Order</option>
                        <option value="Amenity Service">Amenity Service</option>
                        <option value="Room Service">Room Service</option>
                      </select>
                    </div>

                    {currentAdjustment.base_category && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Billing Record *
                          <span className="text-red-500 ml-1">(Required)</span>
                        </label>
                        <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto border border-gray-300 rounded-lg p-3 bg-white">
                          {ordersData[currentAdjustment.base_category] &&
                          ordersData[currentAdjustment.base_category].length > 0 ? (
                            ordersData[currentAdjustment.base_category].map((order, idx) => (
                              <div
                                key={`order-${idx}`}
                                onClick={() => {
                                  setCurrentAdjustment({
                                    ...currentAdjustment,
                                    selected_order: order,
                                    billing_id: order.billing_id,
                                    override_base_amount: order.totalamount,
                                  });
                                  setAmountError('');
                                  if (currentAdjustment.amount) {
                                    const billingRecordAmount = parseFloat(order.totalamount);
                                    let discountAmount = parseFloat(currentAdjustment.amount);
                                    if (currentAdjustment.adjustment_type === 'percentage') {
                                      discountAmount =
                                        (parseFloat(currentAdjustment.amount) / 100) *
                                        billingRecordAmount;
                                    }
                                    if (discountAmount > billingRecordAmount) {
                                      setAmountError(
                                        `Discount cannot exceed ${formatCurrency(billingRecordAmount)}`
                                      );
                                    } else {
                                      setAmountError('');
                                    }
                                  }
                                }}
                                className={`p-3 rounded-lg cursor-pointer transition-colors ${currentAdjustment.selected_order?.billing_id === order.billing_id ? 'border-2 border-black bg-gray-100' : 'border border-gray-200 hover:bg-gray-50'}`}
                              >
                                <div className="flex flex-col space-y-2">
                                  <div className="flex justify-between items-start flex-col sm:flex-row sm:items-center gap-1">
                                    <p className="text-sm font-medium text-gray-900 break-words flex-1 w-full sm:w-auto">
                                      {order.description}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {formatDate(order.created_at)}
                                    </p>
                                  </div>
                                  <div className="grid grid-cols-1 gap-2 text-xs sm:text-sm">
                                    <div className="bg-gray-50 rounded px-3 py-2 flex justify-between items-center">
                                      <span className="text-gray-600 font-medium">
                                        Total Amount:
                                      </span>
                                      <span className="font-bold text-gray-900">
                                        {formatCurrency(order.totalamount)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-sm text-gray-500 italic p-4 text-center">
                              No orders found for {currentAdjustment.base_category}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Adjustment Type
                      </label>
                      <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                        <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors flex-1 bg-white">
                          <input
                            type="radio"
                            checked={currentAdjustment.is_discount}
                            onChange={() =>
                              setCurrentAdjustment({
                                ...currentAdjustment,
                                is_discount: true,
                                billing_type: 'Manual Adjustment - Discount',
                              })
                            }
                            className="w-4 h-4 mr-2 accent-black"
                          />
                          <span className="font-medium text-gray-950">Discount (-)</span>
                        </label>
                        <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors flex-1 bg-white">
                          <input
                            type="radio"
                            checked={!currentAdjustment.is_discount}
                            onChange={() =>
                              setCurrentAdjustment({
                                ...currentAdjustment,
                                is_discount: false,
                                billing_type: 'Manual Adjustment - Charge',
                              })
                            }
                            className="w-4 h-4 mr-2 accent-black"
                          />
                          <span className="font-medium text-gray-950">Markup (+)</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Calculation Type
                      </label>
                      <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                        <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors flex-1 bg-white">
                          <input
                            type="radio"
                            value="flat"
                            checked={currentAdjustment.adjustment_type === 'flat'}
                            onChange={(e) =>
                              setCurrentAdjustment({
                                ...currentAdjustment,
                                adjustment_type: e.target.value,
                              })
                            }
                            className="w-4 h-4 mr-2 accent-black"
                          />
                          <span>Flat (₹)</span>
                        </label>
                        <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors flex-1 bg-white">
                          <input
                            type="radio"
                            value="percentage"
                            checked={currentAdjustment.adjustment_type === 'percentage'}
                            onChange={(e) =>
                              setCurrentAdjustment({
                                ...currentAdjustment,
                                adjustment_type: e.target.value,
                              })
                            }
                            className="w-4 h-4 mr-2 accent-black"
                          />
                          <span>Percentage (%)</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Reason *
                      </label>
                      <select
                        value={currentAdjustment.reason}
                        onChange={(e) =>
                          setCurrentAdjustment({
                            ...currentAdjustment,
                            reason: e.target.value,
                          })
                        }
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-black bg-white text-sm sm:text-base"
                      >
                        <option value="">Select reason</option>
                        <option value="Corporate Discount">Corporate Discount</option>
                        <option value="Weekend Special">Weekend Special</option>
                        <option value="Festive Offer">Festive Offer</option>
                        <option value="Returning Guest">Returning Guest</option>
                        <option value="Staff Discount">Staff Discount</option>
                        <option value="Special Request">Special Request</option>
                        <option value="Complementary">Complementary</option>
                        <option value="General Adjustment">General Adjustment</option>
                        <option value="Ac Fee Exempted">Ac Fee Exempted</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {currentAdjustment.adjustment_type === 'percentage'
                          ? 'Percentage %'
                          : 'Amount (₹)'}
                        {currentAdjustment.is_discount && currentAdjustment.selected_order && (
                          <span className="text-gray-400 text-xs font-normal ml-2">
                            (Max: {formatCurrency(currentAdjustment.selected_order.totalamount)})
                          </span>
                        )}
                      </label>
                      <input
                        type="number"
                        value={currentAdjustment.amount}
                        onChange={handleAmountChange}
                        className={`w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white text-sm sm:text-base ${
                          amountError ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-300'
                        }`}
                        placeholder={
                          currentAdjustment.adjustment_type === 'percentage'
                            ? 'Enter percentage (0-100)'
                            : 'Enter amount'
                        }
                        min="0"
                        max={currentAdjustment.adjustment_type === 'percentage' ? '100' : undefined}
                        step={currentAdjustment.adjustment_type === 'percentage' ? '1' : '0.01'}
                      />
                      {amountError && (
                        <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          {amountError}
                        </p>
                      )}
                      {currentAdjustment.adjustment_type === 'percentage' &&
                        currentAdjustment.selected_order &&
                        currentAdjustment.amount &&
                        !amountError &&
                        parseFloat(currentAdjustment.amount) <= 100 && (
                          <p className="text-xs text-green-600 mt-1">
                            Will apply {currentAdjustment.amount}% ={' '}
                            {formatCurrency(
                              (parseFloat(currentAdjustment.amount) / 100) *
                                parseFloat(currentAdjustment.selected_order.totalamount)
                            )}
                          </p>
                        )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Comment (Optional)
                      </label>
                      <textarea
                        value={currentAdjustment.comment}
                        onChange={(e) =>
                          setCurrentAdjustment({
                            ...currentAdjustment,
                            comment: e.target.value,
                          })
                        }
                        rows="3"
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white resize-none text-sm sm:text-base"
                        placeholder="Enter comment or notes about this adjustment..."
                      />
                    </div>

                    <button
                      onClick={handleAddAdjustment}
                      disabled={!isFormValid()}
                      className={`w-full py-3 rounded-xl font-semibold transition-all shadow-sm text-sm sm:text-base ${isFormValid() ? 'bg-black text-white hover:bg-gray-800 cursor-pointer' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                    >
                      Apply Adjustment
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Discount Voucher Card - Disabled if modifications exist */}
            {invoiceData && (
              <div className="bg-gray-50 rounded-2xl shadow-sm overflow-hidden border border-gray-200">
                <div className="bg-gray-100 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <h2 className="text-xl font-semibold text-gray-900">Discount Voucher</h2>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mt-1 text-left">
                    Transfer the corporate discount amount to other services (Food, Amenities, Room
                    Service).
                  </p>
                </div>
                <div className="p-6">
                  <div className="relative group w-full">
                    <button
                      onClick={() => {
                        if (blocksActions()) {
                          showModificationPopup();
                        } else {
                          setShowVoucherModal(true);
                        }
                      }}
                      disabled={blocksActions()}
                      className={`w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 shadow-sm ${
                        blocksActions()
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-black text-white hover:bg-gray-800'
                      }`}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>{blocksActions() ? 'Voucher Disabled' : 'Create Voucher'}</span>
                    </button>
                    {blocksActions() && (
                      <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded-lg py-2 px-3 w-72 z-[9999] shadow-xl pointer-events-none">
                        <div className="font-medium text-center">{getModificationMessage()}</div>
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Voucher Modal - remains the same but can be disabled */}
            {showVoucherModal && (
              <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
                <div className="bg-gray-100 border border-gray-300 rounded-lg p-6 max-w-md w-full">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-bold text-black">Discount Voucher</h3>
                    <button
                      onClick={() => {
                        setShowVoucherModal(false);
                        setVoucherTargetCategory('');
                        setVoucherReason('');
                      }}
                      className="text-dark-gray hover:text-black"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                  <div className="mb-4">
                    <p className="text-dark-gray text-sm mb-2">
                      <span className="font-medium">Reservation Number:</span> {booking.orderid}
                    </p>
                    <p className="text-dark-gray text-sm mb-4">
                      <span className="font-medium">Guest Name:</span> {booking.firstname}{' '}
                      {booking.lastname}
                    </p>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Target Category *
                      </label>
                      <select
                        value={voucherTargetCategory}
                        onChange={(e) => setVoucherTargetCategory(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-black bg-white"
                      >
                        <option value="">Select category</option>
                        <option value="Food Order">Food Order</option>
                        <option value="Amenity Service">Amenity Service</option>
                        <option value="Room Service">Room Service</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Reason *
                      </label>
                      <input
                        type="text"
                        value={voucherReason}
                        onChange={(e) => setVoucherReason(e.target.value)}
                        placeholder="e.g., Corporate discount allocation, Package adjustment, etc."
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-black"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      onClick={() => {
                        setShowVoucherModal(false);
                        setVoucherTargetCategory('');
                        setVoucherReason('');
                      }}
                      className="px-4 py-2 border border-light-gray rounded-lg text-dark-gray hover:bg-red-500 bg-red-400"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCorporateDiscountTransfer}
                      disabled={transferringVoucher || !voucherTargetCategory || !voucherReason}
                      className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {transferringVoucher ? (
                        <>
                          {' '}
                          <svg
                            className="w-4 h-4 mr-2 animate-spin"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                          </svg>{' '}
                          Processing...
                        </>
                      ) : (
                        'Create Voucher'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Separate Sections for Adjustments, Reversals, and Vouchers */}
            <div className="space-y-6">
              {/* Manual Adjustments Section - Dropdown */}
              {adjustments.length > 0 && (
                <div className="bg-gray-50 rounded-2xl shadow-sm overflow-hidden border border-gray-200">
                  <button
                    onClick={() => setShowAdjustmentsSection(!showAdjustmentsSection)}
                    className="w-full bg-blue-50 px-6 py-4 border-b border-blue-200 hover:bg-blue-100 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <svg
                          className="w-6 h-6 text-blue-700"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <h2 className="text-xl font-semibold text-gray-900">
                          Applied Adjustments ({adjustments.length})
                        </h2>
                      </div>
                      <div className="flex items-center space-x-3">
                        {adjustments.length > 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowCancelAllModal(true);
                            }}
                            className="px-3 py-1 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                          >
                            Cancel All
                          </button>
                        )}
                        <svg
                          className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${showAdjustmentsSection ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 text-left">
                      Discounts or charges applied by staff
                    </p>
                  </button>

                  {showAdjustmentsSection && (
                    <div className="p-6">
                      <div className="space-y-3">
                        {adjustments.map((adj, idx) => (
                          <div
                            key={idx}
                            className="bg-white border border-blue-200 rounded-xl p-4 hover:shadow-sm transition-shadow group"
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="text-sm font-semibold text-gray-900">
                                  {adj.billing_type}
                                </p>
                                {adj.reason && (
                                  <p className="text-sm text-gray-600 mt-2">
                                    <span className="font-medium">Reason:</span> {adj.reason}
                                  </p>
                                )}
                                {adj.comment && (
                                  <p className="text-xs text-gray-500 mt-1 italic">{adj.comment}</p>
                                )}
                                <p className="text-xs text-gray-400 mt-2">
                                  {formatDateTime(adj.created_at)}
                                </p>
                              </div>
                              <div className="flex flex-col items-end space-y-2">
                                <p
                                  className={`text-base font-bold ${adj.billing_type?.includes('Discount') ? 'text-red-600' : 'text-green-600'}`}
                                >
                                  {adj.billing_type?.includes('Discount') ? '-' : '+'}{' '}
                                  {formatCurrency(Math.abs(parseFloat(adj.amount)))}
                                </p>
                                {!blocksActions() && (
                                  <button
                                    onClick={() => {
                                      setSelectedAdjustment(adj);
                                      setShowCancelModal(true);
                                      setCancelReason('');
                                    }}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity text-xs text-white hover:bg-red-600 rounded-md px-2 py-1 bg-red-500 font-medium"
                                  >
                                    Cancel
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Reversals Section - Dropdown */}
              {reversals.length > 0 && (
                <div className="bg-gray-50 rounded-2xl shadow-sm overflow-hidden border border-gray-200">
                  <button
                    onClick={() => setShowReversalsSection(!showReversalsSection)}
                    className="w-full bg-orange-50 px-6 py-4 border-b border-orange-200 hover:bg-orange-100 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <svg
                          className="w-6 h-6 text-orange-700"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                        <h2 className="text-xl font-semibold text-gray-900">
                          Reversals ({reversals.length})
                        </h2>
                      </div>
                      <svg
                        className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${showReversalsSection ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 text-left">
                      Cancelled adjustments that have been reversed
                    </p>
                  </button>

                  {showReversalsSection && (
                    <div className="p-6">
                      <div className="space-y-3">
                        {reversals.map((rev, idx) => (
                          <div
                            key={idx}
                            className="bg-white border border-orange-200 rounded-xl p-4 hover:shadow-sm transition-shadow"
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="text-sm font-semibold text-orange-700">
                                  {rev.billing_type}
                                </p>
                                {rev.reason && (
                                  <p className="text-sm text-gray-600 mt-2">
                                    <span className="font-medium">Reason:</span> {rev.reason}
                                  </p>
                                )}
                                {rev.comment && (
                                  <p className="text-xs text-gray-500 mt-1 italic">{rev.comment}</p>
                                )}
                                {rev.original_billing_id && (
                                  <p className="text-xs text-gray-400 mt-1">
                                    <span className="font-medium">Original Billing ID:</span>{' '}
                                    {rev.original_billing_id}
                                  </p>
                                )}
                                <p className="text-xs text-gray-400 mt-2">
                                  {formatDateTime(rev.created_at)}
                                </p>
                              </div>
                              <div className="flex flex-col items-end">
                                <p className="text-base font-bold text-orange-600">
                                  {formatCurrency(Math.abs(parseFloat(rev.amount)))}
                                </p>
                                <span className="text-xs text-orange-500 italic">Reversed</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Vouchers Section - Dropdown */}
              {vouchers.length > 0 && (
                <div className="bg-gray-50 rounded-2xl shadow-sm overflow-hidden border border-gray-200">
                  <button
                    onClick={() => setShowVouchersSection(!showVouchersSection)}
                    className="w-full bg-green-50 px-6 py-4 border-b border-green-200 hover:bg-green-100 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <svg
                          className="w-6 h-6 text-green-700"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 5v2m0 4v2m0 4v2M5 5h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z"
                          />
                        </svg>
                        <h2 className="text-xl font-semibold text-gray-900">
                          Vouchers ({vouchers.length})
                        </h2>
                      </div>
                      <svg
                        className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${showVouchersSection ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 text-left">
                      Corporate discount vouchers applied to reservation
                    </p>
                  </button>

                  {showVouchersSection && (
                    <div className="p-6">
                      <div className="space-y-3">
                        {vouchers.map((voucher, idx) => (
                          <div
                            key={idx}
                            className="bg-white border border-green-200 rounded-xl p-4 hover:shadow-sm transition-shadow group"
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="text-sm font-semibold text-green-700">
                                    Voucher: {voucher.voucher_code}
                                  </p>
                                  <span
                                    className={`text-xs px-2 py-0.5 rounded-full ${
                                      voucher.status === 'active' || voucher.status === 'applied'
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-100 text-gray-600'
                                    }`}
                                  >
                                    {voucher.status === 'applied' ? 'Active' : voucher.status}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600 mt-2">
                                  <span className="font-medium">Target Category:</span>{' '}
                                  {voucher.target_category}
                                </p>
                                <p className="text-sm text-gray-600">
                                  <span className="font-medium">Reason:</span> {voucher.reason}
                                </p>
                                <p className="text-xs text-gray-400 mt-2">
                                  {formatDateTime(voucher.created_at)}
                                </p>
                              </div>
                              <div className="flex flex-col items-end space-y-2">
                                <p className="text-lg font-bold text-green-600">
                                  {formatCurrency(parseFloat(voucher.amount))}
                                </p>
                                <p className="text-xs text-gray-500">Discount Amount</p>
                                {!blocksActions() && (
                                  <button
                                    onClick={() => {
                                      setSelectedVoucher(voucher);
                                      setShowCancelVoucherModal(true);
                                      setCancelVoucherReason('');
                                    }}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity text-xs text-white hover:bg-red-600 rounded-md px-2 py-1 bg-red-500 font-medium"
                                  >
                                    Cancel
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {refunds.length > 0 && (
                <div className="bg-gray-50 rounded-2xl shadow-sm overflow-hidden border border-gray-200">
                  <button
                    onClick={() => setShowRefundsSection(!showRefundsSection)}
                    className="w-full bg-teal-50 px-6 py-4 border-b border-teal-200 hover:bg-teal-100 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <svg
                          className="w-6 h-6 text-teal-700"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 4V9a2 2 0 00-2-2h-1m-4 0V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h1m4 0h4a2 2 0 002-2v-1m-6 0l-3 3m3-3l3 3"
                          />
                        </svg>
                        <h2 className="text-xl font-semibold text-gray-900">
                          Refunds ({refunds.length})
                        </h2>
                      </div>
                      <svg
                        className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${showRefundsSection ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 text-left">
                      Refunds issued for this reservation
                    </p>
                  </button>

                  {showRefundsSection && (
                    <div className="p-6">
                      <div className="space-y-3">
                        {refunds.map((refund, idx) => (
                          <div
                            key={idx}
                            className="bg-white border border-teal-200 rounded-xl p-4 hover:shadow-sm transition-shadow"
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="text-sm font-semibold text-teal-700">
                                    Refund ID: {refund.refund_id?.slice(0, 8)}
                                    ...
                                  </p>
                                  <span
                                    className={`text-xs px-2 py-0.5 rounded-full ${
                                      refund.status === 'Completed'
                                        ? 'bg-green-100 text-green-800'
                                        : refund.status === 'Pending'
                                          ? 'bg-yellow-100 text-yellow-800'
                                          : 'bg-red-100 text-red-800'
                                    }`}
                                  >
                                    {refund.status || 'Pending'}
                                  </span>
                                </div>
                                {refund.reason && (
                                  <p className="text-sm text-gray-600 mt-2">
                                    <span className="font-medium">Reason:</span> {refund.reason}
                                  </p>
                                )}
                                <p className="text-xs text-gray-400 mt-2">
                                  {formatDateTime(refund.created_at)}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-teal-600">
                                  {formatCurrency(parseFloat(refund.amount))}
                                </p>
                                <p className="text-xs text-gray-500">Refund Amount</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Message when no records */}
              {adjustments.length === 0 &&
                reversals.length === 0 &&
                refunds.length === 0 &&
                vouchers.length === 0 && (
                  <div className="bg-gray-50 rounded-2xl shadow-sm overflow-hidden border border-gray-200">
                    <div className="bg-gray-100 px-6 py-4 border-b border-gray-200">
                      <div className="flex items-center space-x-2">
                        <svg
                          className="w-6 h-6 text-gray-700"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <h2 className="text-xl font-semibold text-gray-900">No Transactions</h2>
                      </div>
                    </div>
                    <div className="p-6 text-center text-gray-500">
                      No manual adjustments, reversals, or vouchers found for this booking.
                    </div>
                  </div>
                )}
            </div>

            {/* Invoice Summary Card */}
            {invoiceData && (
              <div className="bg-gray-50 rounded-2xl shadow-sm p-6 border border-gray-200">
                <div className="flex items-center space-x-2 mb-4">
                  <svg
                    className="w-6 h-6 text-gray-700"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <h2 className="text-xl font-semibold text-gray-900">Invoice Summary</h2>
                </div>
                <div className="space-y-3">
                  {invoiceData.summary?.map((item, idx) => {
                    const amount = parseFloat(item.amount);
                    const label =
                      item.label === 'DISCOUNT ALLOWED' ? 'DISCOUNT APPLIED' : item.label;
                    const isDiscount = item.label === 'DISCOUNT ALLOWED';
                    return (
                      <div
                        key={idx}
                        className="flex justify-between items-center py-2 border-b border-gray-200 last:border-0"
                      >
                        <div>
                          <span className="text-gray-600">{label}</span>
                          {isDiscount && (
                            <span className="text-xs text-gray-400 ml-2">(Excluding taxes)</span>
                          )}
                        </div>
                        <span
                          className={
                            isDiscount ? 'text-red-600 font-medium' : 'text-gray-900 font-medium'
                          }
                        >
                          {isDiscount && amount < 0
                            ? formatCurrency(amount)
                            : formatCurrency(Math.abs(amount))}
                        </span>
                      </div>
                    );
                  })}
                  <div className="pt-2 mt-2 border-t-2 border-gray-300">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-900 font-bold text-lg">Total Amount</span>
                      <span className="text-gray-900 font-bold text-xl">
                        {formatCurrency(invoiceData.grand_total)}
                      </span>
                    </div>
                  </div>
                  {invoiceData.amount_in_words && (
                    <p className="text-xs text-gray-500 mt-3 italic pt-2 border-t border-gray-200">
                      {invoiceData.amount_in_words}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdjustmentDetailsPage;
